import { createContext, useContext, useEffect, useState, ReactNode } from "react"

type Theme = "light" | "dark" | "system"

interface ThemeContextType {
  theme: "light" | "dark"
  themeMode: Theme
  setThemeMode: (mode: Theme) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const getSystemTheme = (): "light" | "dark" => {
  if (typeof window === "undefined") return "light"
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light"
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeMode, setThemeMode] = useState<Theme>(() => {
    // Check localStorage first
    const stored = localStorage.getItem("app-theme-mode")
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored
    }
    return "dark"
  })

  const [theme, setTheme] = useState<"light" | "dark">("dark")
  const [systemTheme, setSystemTheme] = useState<"light" | "dark">(getSystemTheme)

  // Update theme based on mode and system preference
  useEffect(() => {
    let effectiveTheme: "light" | "dark"
    
    if (themeMode === "system") {
      effectiveTheme = systemTheme
    } else {
      effectiveTheme = themeMode
    }

    setTheme(effectiveTheme)

    // Update localStorage
    localStorage.setItem("app-theme-mode", themeMode)

    // Update document class
    const html = document.documentElement
    if (effectiveTheme === "dark") {
      html.classList.add("dark")
      html.style.colorScheme = "dark"
    } else {
      html.classList.remove("dark")
      html.style.colorScheme = "light"
    }
    html.setAttribute("data-theme", effectiveTheme)

    // Set CSS variables for both themes
    const cyan = "#00b8db"
    const cyanDim = "#0088a3"

    if (effectiveTheme === "light") {
      // Base tokens
      html.style.setProperty("--primary", cyan)
      html.style.setProperty("--primary-light", cyanDim)
      html.style.setProperty("--primary-foreground", "#000000")
      html.style.setProperty("--accent-foreground", "#000000")
      // Tab styling for light theme
      html.style.setProperty("--tab-bg", "#e0deda")
      html.style.setProperty("--tab-bg-hover", "#ffffff")
      html.style.setProperty("--tab-bg-active", cyan)
      html.style.setProperty("--tab-text", "#6b6b68")
      html.style.setProperty("--tab-text-active", "#000000")
      // ── v2-dashboard (Ticker page) color tokens ──
      html.style.setProperty("--v2-bg-base",      "#ebebeb")
      html.style.setProperty("--v2-bg-surface",   "#ebebeb")
      html.style.setProperty("--v2-bg-panel",     "#ebebeb")
      html.style.setProperty("--v2-bg-card",      "#f9f9f9")
      html.style.setProperty("--v2-bg-hover",     "#f9f9f9")
      html.style.setProperty("--v2-border",       "#c4c2bc")
      html.style.setProperty("--v2-border-soft",  "#e0deda")
      html.style.setProperty("--v2-text-primary", "#111110")
      html.style.setProperty("--v2-text-muted",   "#6b6b68")
      html.style.setProperty("--v2-text-dim",     "#9e9c98")
      html.style.setProperty("--v2-scrollbar-track", "#ebebeb")
      html.style.setProperty("--v2-scrollbar-thumb", "#c4c2bc")
      html.style.setProperty("--v2-scrollbar-hover", "#a8a6a0")
      html.style.setProperty("--v2-axis-label",   "#111110");
      html.style.setProperty("--v2-grid-line",    "#e0deda")
      html.style.setProperty("--v2-tooltip-bg",   "#ffffff")
      html.style.setProperty("--v2-tooltip-text", "#111110")
      html.style.setProperty("--v2-tooltip-border","#c4c2bc")
    } else {
      // Base tokens
      html.style.setProperty("--primary", cyan)
      html.style.setProperty("--primary-light", cyanDim)
      html.style.setProperty("--primary-foreground", "#000000")
      html.style.setProperty("--accent-foreground", "#000000")
      // Tab styling for dark theme
      html.style.setProperty("--tab-bg", "#1a1a1a")
      html.style.setProperty("--tab-bg-hover", "#2e2e2e")
      html.style.setProperty("--tab-bg-active", cyan)
      html.style.setProperty("--tab-text", "#a0a0a0")
      html.style.setProperty("--tab-text-active", "#000000")
      html.style.setProperty("--sidebar-primary-foreground", "#000000")
      // ── v2-dashboard (Ticker page) color tokens ──
      html.style.setProperty("--v2-bg-base",      "#111111")
      html.style.setProperty("--v2-bg-surface",   "#1a1a1a")
      html.style.setProperty("--v2-bg-panel",     "#1a1a1a")
      html.style.setProperty("--v2-bg-card",      "#222222")
      html.style.setProperty("--v2-bg-hover",     "#2e2e2e")
      html.style.setProperty("--v2-border",       "rgba(255,255,255,0.08)")
      html.style.setProperty("--v2-border-soft",  "rgba(255,255,255,0.08)")
      html.style.setProperty("--v2-text-primary", "#e0e0e0")
      html.style.setProperty("--v2-text-muted",   "#a0a0a0")
      html.style.setProperty("--v2-text-dim",     "#555555")
      html.style.setProperty("--v2-scrollbar-track", "#1a1a1a")
      html.style.setProperty("--v2-scrollbar-thumb", "#2e2e2e")
      html.style.setProperty("--v2-scrollbar-hover", "rgba(255,255,255,0.14)")
      html.style.setProperty("--v2-axis-label",   "#e0e0e0");
      html.style.setProperty("--v2-grid-line",    "rgba(255,255,255,0.08)")
      html.style.setProperty("--v2-tooltip-bg",   "#222222")
      html.style.setProperty("--v2-tooltip-text", "#e0e0e0")
      html.style.setProperty("--v2-tooltip-border","rgba(255,255,255,0.08)")
    }
  }, [themeMode, systemTheme])

  // Listen for system theme changes
  useEffect(() => {
    if (themeMode !== "system") return

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    setSystemTheme(mediaQuery.matches ? "dark" : "light")
    const handleChange = () => setSystemTheme(mediaQuery.matches ? "dark" : "light")

    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [themeMode])

  const toggleTheme = () => {
    if (themeMode === "system") setThemeMode(theme === "dark" ? "light" : "dark")
    else if (themeMode === "light") setThemeMode("dark")
    else setThemeMode("light")
  }

  return (
    <ThemeContext.Provider value={{ theme, themeMode, setThemeMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error("useTheme must be used within ThemeProvider")
  }
  return context
}
